/**
 * Input sanitization and validation utilities
 */

// Basic HTML sanitization to prevent XSS
export function sanitizeHtml(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
}

// Email validation
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

// Password validation
export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// Search query sanitization
export function sanitizeSearchQuery(query: string): string {
  return sanitizeHtml(query).slice(0, 100); // Limit to 100 characters
}

// Game ID validation
export function isValidGameId(id: string): boolean {
  return /^[a-zA-Z0-9-_]{1,50}$/.test(id);
}

// Name validation for user registration
export function validateName(name: string): {
  isValid: boolean;
  error?: string;
} {
  const trimmed = name.trim();
  
  if (trimmed.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters long' };
  }
  
  if (trimmed.length > 50) {
    return { isValid: false, error: 'Name must be less than 50 characters' };
  }
  
  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
    return { isValid: false, error: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
  }
  
  return { isValid: true };
}

// Generic string validation
export function validateString(input: string, options: {
  minLength?: number;
  maxLength?: number;
  allowEmpty?: boolean;
  pattern?: RegExp;
}): {
  isValid: boolean;
  error?: string;
} {
  const {
    minLength = 0,
    maxLength = 1000,
    allowEmpty = false,
    pattern
  } = options;
  
  const trimmed = input.trim();
  
  if (!allowEmpty && trimmed.length === 0) {
    return { isValid: false, error: 'This field is required' };
  }
  
  if (trimmed.length < minLength) {
    return { isValid: false, error: `Must be at least ${minLength} characters long` };
  }
  
  if (trimmed.length > maxLength) {
    return { isValid: false, error: `Must be less than ${maxLength} characters` };
  }
  
  if (pattern && !pattern.test(trimmed)) {
    return { isValid: false, error: 'Invalid format' };
  }
  
  return { isValid: true };
}
